import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { sellerService } from '@/services/seller';
import { useSellerStore } from '@/store/seller';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import type { OrderWithItems } from '@/store/seller';

export function DesktopOrderRefundReasonPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const { updateOrderStatus } = useSellerStore();

  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(true);

  useEffect(() => {
    const loadOrder = async () => {
      if (!orderId) return;

      try {
        const result = await sellerService.getOrderDetail(orderId);
        if (result.success && result.data) {
          setOrder(result.data);
        }
      } catch (error) {
        console.error('Order load error:', error);
      } finally {
        setLoadingOrder(false);
      }
    };

    loadOrder();
  }, [orderId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      showToast('환불 사유를 입력해주세요');
      return;
    }

    if (!orderId) {
      showToast('주문 정보를 찾을 수 없습니다');
      return;
    }

    setLoading(true);
    try {
      const result = await sellerService.cancelOrder(orderId, reason.trim());

      if (result.success) {
        // 스토어 상태 업데이트
        updateOrderStatus(orderId, 'CANCELLED');
        
        showToast('환불 처리가 완료되었습니다');
        navigate('/seller/home');
      } else {
        showToast(result.error || '환불 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('Order refund error:', error);
      showToast('환불 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/seller/orders/${orderId}`);
  };

  if (loadingOrder) {
    return (
      <DesktopSellerPageContainer>
        <div className="min-h-[calc(100vh-72px)] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">주문 정보를 불러오는 중...</p>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-[800px] mx-auto">
        {/* 헤더 */}
        <div className="flex items-center mb-8">
          <button
            onClick={handleCancel}
            className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-[28px] font-bold text-gray-800">환불 처리</h1>
        </div>

        {/* 주문 정보 카드 */}
        {order && (
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-[22px] font-bold text-gray-800 mb-6">주문 정보</h2>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-gray-600 mb-2">주문번호</p>
                <p className="text-lg font-semibold">{order.order_number}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-2">주문자</p>
                <p className="text-lg font-semibold">{order.buyer_nickname}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-2">주문금액</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(order.total_amount)}원</p>
              </div>
              <div>
                <p className="text-gray-600 mb-2">주문상태</p>
                <p className="text-lg font-semibold">
                  {order.status === 'READY' ? '준비 완료' : 
                   order.status === 'COMPLETED' ? '완료' : order.status}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 안내 메시지 */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
          <p className="text-red-800 text-lg leading-relaxed">
            환불 처리 시 결제가 취소되며, 고객에게 환불 사유가 전달됩니다. 
            명확한 사유는 고객의 이해와 만족도 향상에 도움이 됩니다.
          </p>
        </div>

        {/* 사유 입력 */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <label className="block text-lg font-semibold text-gray-700 mb-4">
            환불 사유 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="환불 사유를 입력해주세요"
            className="w-full h-40 px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            maxLength={500}
          />
          <div className="text-right text-gray-500 mt-2">
            {reason.length}/500
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={handleCancel}
            className="py-4 px-6 bg-gray-500 text-white rounded-lg font-medium text-lg hover:bg-gray-600 transition-colors"
          >
            돌아가기
          </button>
          <button
            onClick={handleSubmit}
            disabled={!reason.trim() || loading}
            className={`py-4 px-6 rounded-lg font-medium text-lg transition-colors ${
              reason.trim() && !loading
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? '처리 중...' : '환불 처리'}
          </button>
        </div>
      </div>

      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}